import React from "react";
import { useTranslation } from "react-i18next";
import { SettingSection } from "./controls/SettingSection";
import { VerticalAlignField } from "./controls/VerticalAlignField";
import { Panel } from "./Panel";
import {
  SurfaceAppearanceSection,
  SurfaceCornerRoundingFields,
} from "./SurfaceAppearanceFields";
import { EmailBuilderSettingsPaddingSection } from "./controls/EmailBuilderSettingsPaddingSection";
import { COLUMN_SETTINGS_DEFAULT_EXPANDED } from "./settingsPanelDefaults";
import {
  isCornerRoundingInactive,
  isPaddingInactive,
  surfaceCornerRoundingAddDefault,
  surfacePaddingAddDefault,
} from "./settingsSectionActivation";
import {
  baselineColumnBackgroundColor,
  baselineColumnBorder,
  baselineColumnBorderRadius,
  baselineColumnPadding,
} from "../domain/baselines";
import { findColumn, findRow } from "../domain/find";
import { translateColumnNumberLabel } from "../domain/labels";
import { selectDocument } from "../domain/selectors";
import { useEditorActions, useEditorStore } from "../domain/redux/hooks";

type ColumnSettingsPanelProps = {
  rowId: string;
  columnId: string;
};

export function ColumnSettingsPanel({
  rowId,
  columnId,
}: ColumnSettingsPanelProps) {
  const { t } = useTranslation();
  const document = useEditorStore(selectDocument);
  const { updateColumn } = useEditorActions();
  const row = findRow(document, rowId);
  const column = findColumn(document, rowId, columnId);
  if (!row || !column) {
    return null;
  }
  const columnIndex = row.columns.findIndex(item => item.id === column.id);
  const columnCount = row.columns.length;
  const defaultPadding = baselineColumnPadding(columnIndex, columnCount);
  const defaultBorder = baselineColumnBorder();
  const defaultRadius = baselineColumnBorderRadius();
  const appearanceBaselines = {
    backgroundColor: baselineColumnBackgroundColor(),
    border: defaultBorder,
    borderRadius: defaultRadius,
  };

  return (
    <Panel
      title={t("{{column}} settings", {
        column: translateColumnNumberLabel(t, document, rowId, columnId),
      })}
      defaultExpandedSectionIds={COLUMN_SETTINGS_DEFAULT_EXPANDED}
    >
      <SettingSection sectionId="alignment" title={t("Alignment")}>
        <VerticalAlignField
          value={column.verticalAlign}
          onChange={verticalAlign =>
            updateColumn(rowId, column.id, { verticalAlign })
          }
        />
      </SettingSection>

      <SettingSection
        sectionId="padding"
        title={t("Padding")}
        activation={{
          mode: "optional",
          isActive: !isPaddingInactive(column.padding),
          onAdd: () =>
            updateColumn(rowId, column.id, { padding: surfacePaddingAddDefault() }),
        }}
      >
        <EmailBuilderSettingsPaddingSection
          fieldLabel={t("Column padding (px)")}
          value={column.padding}
          defaultValue={defaultPadding}
          onChange={padding => {
            updateColumn(rowId, column.id, { padding: { ...padding } });
          }}
        />
      </SettingSection>

      <SettingSection
        sectionId="corner-rounding"
        title={t("Corner rounding")}
        activation={{
          mode: "optional",
          isActive: !isCornerRoundingInactive(column.borderRadius),
          onAdd: () =>
            updateColumn(rowId, column.id, {
              borderRadius: surfaceCornerRoundingAddDefault(),
            }),
        }}
      >
        <SurfaceCornerRoundingFields
          value={column}
          baselines={appearanceBaselines}
          onChange={patch => updateColumn(rowId, column.id, patch)}
        />
      </SettingSection>

      <SettingSection sectionId="appearance" title={t("Appearance")}>
        <SurfaceAppearanceSection
          value={column}
          baselines={appearanceBaselines}
          allowTransparentBackground
          showBackgroundImage
          onChange={patch => updateColumn(rowId, column.id, patch)}
        />
      </SettingSection>
    </Panel>
  );
}
